/**
 * BacktestResults — Bottom panel for the Strategy Builder.
 *
 * Shows backtest results: MetricsGrid, EquityChart, TodayActivity,
 * and HistoricalAllocations. Collapsible via a header bar.
 */

import { useState } from 'react';
import clsx from 'clsx';
import type { BacktestResult, TimeRange } from '../../stores/backtestStore';
import { MetricsGrid } from '../MetricsGrid';
import { EquityChart } from '../charts/EquityChart';
import { TodayActivity } from '../TodayActivity';
import { HistoricalAllocations } from '../HistoricalAllocations';

interface BacktestResultsProps {
  result: BacktestResult;
  onClose: () => void;
}

export function BacktestResults({ result, onClose }: BacktestResultsProps) {
  const [timeRange, setTimeRange] = useState<TimeRange>('1Y');
  const [showActivity, setShowActivity] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);

  return (
    <div className="border-t border-surface-700 bg-surface-900/95 flex flex-col shrink-0">
      {/* Header bar */}
      <div className="flex items-center justify-between px-4 py-2 bg-surface-800/60 border-b border-surface-700/50">
        <button
          onClick={() => setIsCollapsed(!isCollapsed)}
          className="flex items-center gap-2 text-sm font-semibold text-surface-100 hover:text-primary-400 transition-colors"
        >
          <svg
            className={clsx('w-4 h-4 transition-transform', isCollapsed ? '-rotate-90' : 'rotate-0')}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
          Backtest Results
          <span className="text-xs font-normal text-surface-500">
            — {result.strategyName} ({result.startDate} to {result.endDate})
          </span>
        </button>

        <div className="flex items-center gap-2">
          {/* Today's Activity toggle */}
          <button
            onClick={() => setShowActivity(!showActivity)}
            className={clsx(
              'text-xs px-2.5 py-1 rounded-md transition-colors',
              showActivity
                ? 'bg-accent-500/20 text-accent-400 ring-1 ring-accent-500/30'
                : 'text-surface-400 hover:text-surface-200 hover:bg-surface-700/50',
            )}
          >
            Today's Activity
          </button>
          {/* Close */}
          <button
            onClick={onClose}
            className="text-surface-500 hover:text-surface-300 p-1 rounded-md hover:bg-surface-700/50 transition-colors"
            title="Close results"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      {/* Collapsible content */}
      {!isCollapsed && (
        <div className="overflow-y-auto custom-scrollbar" style={{ maxHeight: '60vh' }}>
          <div className="px-4 py-4 space-y-4">
            {/* Metrics */}
            <MetricsGrid
              metrics={result.metrics}
              initialCapital={result.initialCapital}
              finalValue={result.finalValue}
              totalSlippage={result.totalSlippage}
              totalRegulatoryFees={result.totalRegulatoryFees}
            />

            {/* Equity Chart */}
            <EquityChart
              data={result.equity}
              timeRange={timeRange}
              onTimeRangeChange={setTimeRange}
            />

            {/* Today's Activity */}
            {showActivity && result.todayAllocation && (
              <TodayActivity allocation={result.todayAllocation} />
            )}

            {/* Backtest Data */}
            {result.allocations && result.allocations.length > 0 && (
              <HistoricalAllocations allocations={result.allocations} />
            )}
          </div>
        </div>
      )}
    </div>
  );
}
